import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as CampaignsAPI from "../../services/api/Campaigns";
import * as CampaignContentAPI from "../../services/api/CampaignContent";
import { DMCampaignType } from "@pollyslack/hub/src/types";
import DatePicker from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";
import MenuBar from "../../components/MenuBar";
import { Tooltip } from "reactstrap";
import { BATCH_SIZE } from "./constants";
import {
  MAX_SEND_DAY,
  MIN_SEND_DAY,
  ScheduleRow,
  buildSchedulePreview,
  chunkArray,
  getEligibleSendDates,
  isWeekend,
  parseRecipientsCsv,
} from "./scheduleUtils";
import { SequenceTab } from "./SequenceTab";
import { CampaignBuilderTab } from "./CampaignBuilderTab";
import { SequenceBuilderTab } from "./SequenceBuilderTab";
import {
  DEFAULT_SEND_SCHEDULE,
  SendSchedule,
  SendTimeInput,
  describeSendSchedule,
  sendScheduleError,
  sendScheduleParams,
} from "./SendTimeInput";

const Spinner: React.FC = () => (
  <span
    className="spinner-border spinner-border-sm mr-2"
    role="status"
    aria-hidden="true"
  />
);

export const CampaignsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "individual" | "sequence" | "builder" | "sequenceBuilder"
  >("individual");
  const [campaignType, setCampaignType] = useState<string>(
    DMCampaignType.DMCampaign_v1,
  );
  const [dynamicCampaigns, setDynamicCampaigns] = useState<
    { identifier: string; headerText: string }[]
  >([]);
  const [campaignBuilderEnabled, setCampaignBuilderEnabled] = useState(false);
  const [userIdsText, setUserIdsText] = useState("");

  useEffect(() => {
    CampaignContentAPI.getConfig()
      .then((config) => {
        setCampaignBuilderEnabled(config.campaignBuilderEnabled);
        if (config.campaignBuilderEnabled) {
          CampaignContentAPI.getActiveIdentifiers()
            .then(setDynamicCampaigns)
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);
  const [sendDate, setSendDate] = useState<moment.Moment>(() => {
    // Tomorrow, or the Monday after when tomorrow is a weekend
    const date = moment().add(1, "day").startOf("day");
    while (isWeekend(date)) {
      date.add(1, "day");
    }
    return date;
  });
  const [scheduleMode, setScheduleMode] = useState<"single" | "range">(
    "single",
  );
  const [sendSchedule, setSendSchedule] = useState<SendSchedule>(
    DEFAULT_SEND_SCHEDULE,
  );
  const [rangeStartDate, setRangeStartDate] = useState<moment.Moment>(
    moment().add(1, "day").startOf("day"),
  );
  const [rangeEndDate, setRangeEndDate] = useState<moment.Moment>(
    moment().add(1, "day").startOf("day").add(13, "days"),
  );
  // Per-user Send Date day values from the last confirmed CSV upload
  const [csvSchedule, setCsvSchedule] = useState<{
    fileName: string;
    dayByUserId: Record<string, number>;
    invalidDayUserIds: string[];
  } | null>(null);
  // Editable day -> date mapping shown in the confirmation modal (range mode)
  const [scheduleRows, setScheduleRows] = useState<ScheduleRow[] | null>(null);

  // Validation function to block past dates
  const isValidDate = (current: moment.Moment) => {
    const today = moment().startOf("day");
    return current.isSameOrAfter(today);
  };

  // A DM's send date: not in the past and not on a weekend (PS-5851)
  const isValidSendDay = (current: moment.Moment) =>
    isValidDate(current) && !isWeekend(current);

  const isValidRangeEndDate = (current: moment.Moment) => {
    return (
      isValidDate(current) &&
      (!rangeStartDate || current.isSameOrAfter(rangeStartDate, "day"))
    );
  };

  const rangeWeekdayCount = getEligibleSendDates(
    rangeStartDate,
    rangeEndDate,
  ).length;
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<
    "add" | "remove" | "optout" | "delete-messages"
  >("add");
  const [confirmationDetails, setConfirmationDetails] = useState<{
    userCount: number;
    campaignType?: string;
    sendDate?: string;
    sendSchedule?: SendSchedule;
    bypassPaidPlanCheck?: boolean;
    scheduleDefaultedCount?: number;
  } | null>(null);
  const [csvConfirmation, setCsvConfirmation] = useState<{
    fileName: string;
    userCount: number;
    userIds: string[];
    hasSendDateColumn: boolean;
    dayByUserId: Record<string, number>;
    invalidDayUserIds: string[];
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [csvTooltipOpen, setCsvTooltipOpen] = useState(false);
  const [bypassPaidPlanCheck, setBypassPaidPlanCheck] = useState(false);

  const getUserIds = () => {
    return userIdsText.split(/[\s,]+/).filter(Boolean);
  };

  const handleAddCampaign = async () => {
    successMessage && setSuccessMessage(null);
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    setLoadingAction("add");

    try {
      // Fire all batch requests in parallel without waiting; surface any
      // late failure as a toast since the UI has already moved on
      chunkArray(userIds, BATCH_SIZE).forEach((batch) =>
        CampaignsAPI.addDMCampaign(
          batch,
          campaignType,
          sendDate?.format("YYYY-MM-DD"),
          bypassPaidPlanCheck,
          sendScheduleParams(sendSchedule),
        ).catch((error: any) =>
          toast.error(
            `Add batch of ${batch.length} users failed: ${error.message}`,
          ),
        ),
      );

      setSuccessMessage(
        `Request completed. Results will be posted in the #project-dm-campaign channel.`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  /**
   * Adds users to the campaign spread across the confirmed day -> date
   * schedule (range mode). Users are grouped by their resolved send date and
   * each group is fired in batches, like the single-date add flow.
   */
  const handleAddCampaignRange = async () => {
    successMessage && setSuccessMessage(null);
    if (!scheduleRows || !csvSchedule) {
      toast.error("No schedule available. Please try again.");
      return;
    }
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    setLoadingAction("add");

    const dateByDay = new Map(
      scheduleRows.map((row) => [row.day, row.date] as const),
    );
    const userIdsByDate = new Map<string, string[]>();
    for (const userId of userIds) {
      const day = csvSchedule.dayByUserId[userId] || MIN_SEND_DAY;
      const date = dateByDay.get(day);
      if (!date) {
        continue;
      }
      const dateString = date.format("YYYY-MM-DD");
      const group = userIdsByDate.get(dateString) || [];
      group.push(userId);
      userIdsByDate.set(dateString, group);
    }

    try {
      // Fire all batch requests in parallel without waiting; surface any
      // late failure as a toast since the UI has already moved on
      userIdsByDate.forEach((ids, dateString) => {
        chunkArray(ids, BATCH_SIZE).forEach((batch) =>
          CampaignsAPI.addDMCampaign(
            batch,
            campaignType,
            dateString,
            bypassPaidPlanCheck,
            sendScheduleParams(sendSchedule),
          ).catch((error: any) =>
            toast.error(
              `Batch of ${batch.length} users for ${dateString} failed: ${error.message}`,
            ),
          ),
        );
      });

      setSuccessMessage(
        `Request completed. DMs scheduled across ${userIdsByDate.size} day(s). Results will be posted in the #project-dm-campaign channel.`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingAction(null);
      setScheduleRows(null);
    }
  };

  const handleRemoveCampaign = async () => {
    successMessage && setSuccessMessage(null);
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    setLoadingAction("remove");
    try {
      // Fire all batch requests in parallel without waiting; surface any
      // late failure as a toast since the UI has already moved on
      chunkArray(userIds, BATCH_SIZE).forEach((batch) =>
        CampaignsAPI.removeDMCampaign(batch, campaignType).catch((error: any) =>
          toast.error(
            `Remove batch of ${batch.length} users failed: ${error.message}`,
          ),
        ),
      );

      setSuccessMessage(
        `Request completed. Results will be posted in the #project-dm-campaign channel.`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleOptOut = async () => {
    successMessage && setSuccessMessage(null);
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    setLoadingAction("optout");
    try {
      const result = await CampaignsAPI.optOutUsers(userIds);
      const notFoundMessage =
        result.notFoundUserIds.length > 0
          ? `\nNot found user IDs: ${result.notFoundUserIds.join(", ")}`
          : "";
      setSuccessMessage(
        `Request to opt out ${result.optedOut} users completed.
        \n${result.notFound} users were not found.${notFoundMessage}`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeleteMessages = async () => {
    successMessage && setSuccessMessage(null);
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    setLoadingAction("delete-messages");
    try {
      const result = await CampaignsAPI.deleteDMCampaignMessages(
        userIds,
        campaignType,
      );
      const notFoundMessage =
        result.notFoundUserIds.length > 0
          ? `\nNot found user IDs: ${result.notFoundUserIds.join(", ")}`
          : "";
      const failedMessage =
        result.failedUserIds.length > 0
          ? `\nFailed deletion user IDs: ${result.failedUserIds.join(", ")}`
          : "";
      setSuccessMessage(
        `Request to delete messages completed.
        \n${result.messagesDeleted} messages deleted for ${result.usersProcessed} users.
        \n${result.notFound} users were not found.${notFoundMessage}
        \n${result.failedDeletions} users had failed deletions.${failedMessage}`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleConfirmationRequest = () => {
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }

    if (selectedAction === "add" && sendScheduleError(sendSchedule)) {
      toast.error(sendScheduleError(sendSchedule));
      return;
    }

    if (
      selectedAction === "add" &&
      scheduleMode === "single" &&
      moment.isMoment(sendDate) &&
      isWeekend(sendDate)
    ) {
      toast.error("The send date is on a weekend; pick a weekday.");
      return;
    }

    let scheduleDefaultedCount: number | undefined;
    if (selectedAction === "add" && scheduleMode === "range") {
      if (!csvSchedule) {
        toast.error(
          "Date range scheduling requires a CSV upload with a Send Date column.",
        );
        return;
      }
      if (rangeEndDate.isBefore(rangeStartDate, "day")) {
        toast.error("End date cannot be before the start date.");
        return;
      }
      const eligibleDates = getEligibleSendDates(rangeStartDate, rangeEndDate);
      const { preview, error } = buildSchedulePreview(
        userIds,
        csvSchedule.dayByUserId,
        eligibleDates,
      );
      if (error || !preview) {
        toast.error(error || "Failed to build the schedule.");
        return;
      }
      setScheduleRows(preview.rows);
      scheduleDefaultedCount = preview.defaultedUserCount;
    } else {
      setScheduleRows(null);
    }

    setConfirmationDetails({
      userCount: userIds.length,
      campaignType:
        selectedAction === "add" ||
        selectedAction === "remove" ||
        selectedAction === "delete-messages"
          ? campaignType
          : undefined,
      sendDate:
        selectedAction === "add" && scheduleMode === "single" && sendDate
          ? sendDate.format("LL")
          : undefined,
      sendSchedule: selectedAction === "add" ? sendSchedule : undefined,
      bypassPaidPlanCheck:
        selectedAction === "add" ? bypassPaidPlanCheck : undefined,
      scheduleDefaultedCount,
    });
  };

  const executeAction = () => {
    setConfirmationDetails(null);
    if (selectedAction === "add") {
      if (scheduleMode === "range" && scheduleRows) {
        handleAddCampaignRange();
      } else {
        handleAddCampaign();
      }
    } else if (selectedAction === "remove") {
      handleRemoveCampaign();
    } else if (selectedAction === "optout") {
      handleOptOut();
    } else if (selectedAction === "delete-messages") {
      handleDeleteMessages();
    }
  };

  const updateScheduleRowDate = (
    index: number,
    value: moment.Moment | string,
  ) => {
    // react-datetime emits a string while the typed value is unparseable
    if (!moment.isMoment(value) || !isValidSendDay(value)) {
      return;
    }
    const date = value.clone().startOf("day");
    setScheduleRows((rows) =>
      rows
        ? rows.map((row, i) => (i === index ? { ...row, date } : row))
        : rows,
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseRecipientsCsv(text);
      if (!parsed) {
        toast.error("CSV file must have a header and at least one user ID.");
        return;
      }
      if (parsed.userIds.length === 0) {
        toast.error("No user IDs found in the CSV file.");
        return;
      }

      setCsvConfirmation({
        fileName: file.name,
        userCount: parsed.userIds.length,
        userIds: parsed.userIds,
        hasSendDateColumn: parsed.hasSendDateColumn,
        dayByUserId: parsed.dayByUserId,
        invalidDayUserIds: parsed.invalidDayUserIds,
      });
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleConfirmCSV = () => {
    if (csvConfirmation) {
      setUserIdsText(csvConfirmation.userIds.join("\n"));
      setCsvSchedule(
        csvConfirmation.hasSendDateColumn
          ? {
              fileName: csvConfirmation.fileName,
              dayByUserId: csvConfirmation.dayByUserId,
              invalidDayUserIds: csvConfirmation.invalidDayUserIds,
            }
          : null,
      );
      setCsvConfirmation(null);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <MenuBar />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="card shadow border-0">
              <div className="card-header bg-transparent pt-4 px-4 border-0">
                <h3 className="mb-0">Campaign Management</h3>
                <p className="text-muted small">
                  Add, remove, opt-out users from DM campaigns, or delete sent
                  campaign messages.
                </p>
                <div className="text-danger font-weight-600 mb-3">
                  <i className="fa fa-exclamation-triangle"></i>{" "}
                  <strong>WARNING!!!</strong> Do not use this functionality
                  without <b>explicit permission from Polly Leadership</b>.
                  Please note that all activity in this Admin app is logged and
                  monitored as part of our security requirements.
                </div>

                {/* Main Tabs: Individual vs Sequence */}
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "individual" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("individual")}
                    >
                      Individual DMs
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "sequence" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("sequence")}
                    >
                      Sequence Scheduling
                    </button>
                  </li>
                  {campaignBuilderEnabled && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "builder" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("builder")}
                      >
                        Campaign Builder
                      </button>
                    </li>
                  )}
                  {campaignBuilderEnabled && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "sequenceBuilder" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("sequenceBuilder")}
                      >
                        Sequence Builder
                      </button>
                    </li>
                  )}
                </ul>
              </div>
              <div className="card-body p-4">
                {activeTab === "sequenceBuilder" ? (
                  <SequenceBuilderTab />
                ) : activeTab === "builder" ? (
                  <CampaignBuilderTab />
                ) : activeTab === "sequence" ? (
                  <SequenceTab />
                ) : (
                  <>
                    <div className="d-flex mb-4" role="group">
                      <button
                        type="button"
                        className={`btn w-100 mr-2 ${
                          selectedAction === "add"
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => setSelectedAction("add")}
                      >
                        Add to Campaign
                      </button>
                      <button
                        type="button"
                        className={`btn w-100 mr-2 ${
                          selectedAction === "remove"
                            ? "btn-warning text-white"
                            : "btn-outline-warning text-dark"
                        }`}
                        onClick={() => setSelectedAction("remove")}
                      >
                        Remove from Campaign
                      </button>
                      <button
                        type="button"
                        className={`btn w-100 mr-2 ${
                          selectedAction === "optout"
                            ? "btn-danger"
                            : "btn-outline-danger"
                        }`}
                        onClick={() => setSelectedAction("optout")}
                      >
                        Opt Out
                      </button>
                      <button
                        type="button"
                        className={`btn w-100 ${
                          selectedAction === "delete-messages"
                            ? "btn-info text-white"
                            : "btn-outline-info text-dark"
                        }`}
                        onClick={() => setSelectedAction("delete-messages")}
                      >
                        Delete Messages
                      </button>
                    </div>

                    <div className="form-group mb-4">
                      <label
                        htmlFor="userIdsTextarea"
                        className="font-weight-bold mb-2"
                      >
                        User IDs
                      </label>
                      <div className="d-flex align-items-center mb-2">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={triggerFileInput}
                        >
                          Upload
                        </button>
                        <i
                          className="fa fa-info-circle text-muted ml-2"
                          id="csv-upload-info"
                          style={{ cursor: "pointer" }}
                        ></i>
                        <Tooltip
                          placement="right"
                          isOpen={csvTooltipOpen}
                          target="csv-upload-info"
                          toggle={() => setCsvTooltipOpen(!csvTooltipOpen)}
                        >
                          Please upload a CSV file with user IDs in the first
                          column and a header row. For date range scheduling,
                          include a &quot;Send Date&quot; column with day values
                          from 1 to 10.
                        </Tooltip>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept=".csv"
                          style={{ display: "none" }}
                        />
                      </div>
                      <textarea
                        id="userIdsTextarea"
                        className="form-control"
                        rows={5}
                        value={userIdsText}
                        onChange={(e) => {
                          setUserIdsText(e.target.value);
                          // Manual edits invalidate the per-user Send Date
                          // mapping from the CSV — otherwise edited IDs would
                          // silently default to Day 1 in range scheduling
                          if (csvSchedule) {
                            setCsvSchedule(null);
                            toast.info(
                              "User IDs edited — the CSV Send Date mapping was cleared. Re-upload the CSV to use date range scheduling.",
                            );
                          }
                        }}
                        placeholder="Enter user mongo IDs separated by commas, spaces, or new lines, or upload a CSV."
                      />
                    </div>

                    {(selectedAction === "add" ||
                      selectedAction === "remove" ||
                      selectedAction === "delete-messages") && (
                      <div className="form-group mb-4">
                        <label
                          htmlFor="campaignTypeSelect"
                          className="font-weight-bold mb-2"
                        >
                          Campaign Type
                        </label>
                        <select
                          id="campaignTypeSelect"
                          className="form-control"
                          value={campaignType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setCampaignType(newType);
                            if (!newType.startsWith("sequence")) {
                              setBypassPaidPlanCheck(false);
                            }
                          }}
                        >
                          <optgroup label="Legacy Campaigns">
                            {Object.values(DMCampaignType).map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </optgroup>
                          {dynamicCampaigns.length > 0 && (
                            <optgroup label="Dynamic Campaigns">
                              {dynamicCampaigns.map((c) => (
                                <option key={c.identifier} value={c.identifier}>
                                  {c.identifier} — {c.headerText}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                    )}
                    {selectedAction === "add" && (
                      <div className="form-group mb-4">
                        <label className="font-weight-bold mb-2 d-block">
                          Scheduling
                        </label>
                        <div className="border rounded p-3">
                          <div
                            className="btn-group btn-group-sm mb-3"
                            role="group"
                            aria-label="Schedule mode"
                          >
                            <button
                              type="button"
                              className={`btn ${
                                scheduleMode === "single"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              onClick={() => setScheduleMode("single")}
                            >
                              Single send date
                            </button>
                            <button
                              type="button"
                              className={`btn ${
                                scheduleMode === "range"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              onClick={() => setScheduleMode("range")}
                            >
                              Date range (CSV)
                            </button>
                          </div>
                          {scheduleMode === "single" ? (
                            <div className="form-row align-items-end">
                              <div className="col-md-4">
                                <label htmlFor="sendDate" className="mb-2">
                                  Send Date (optional)
                                </label>
                                <DatePicker
                                  value={sendDate}
                                  onChange={(date) =>
                                    setSendDate(date as moment.Moment)
                                  }
                                  timeFormat={false}
                                  isValidDate={isValidSendDay}
                                  inputProps={{
                                    id: "sendDate",
                                    className: "form-control",
                                  }}
                                />
                              </div>
                              <div className="col-md-8">
                                <small className="text-muted">
                                  All users receive the DM on this date, at the
                                  send time below in their own timezone. Past
                                  dates and weekends cannot be selected.
                                </small>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="form-row">
                                <div className="col-md-4">
                                  <label
                                    htmlFor="rangeStartDate"
                                    className="mb-2"
                                  >
                                    Start Date
                                  </label>
                                  <DatePicker
                                    value={rangeStartDate}
                                    onChange={(date) =>
                                      setRangeStartDate(date as moment.Moment)
                                    }
                                    timeFormat={false}
                                    isValidDate={isValidDate}
                                    inputProps={{
                                      id: "rangeStartDate",
                                      className: "form-control",
                                    }}
                                  />
                                </div>
                                <div className="col-md-4">
                                  <label
                                    htmlFor="rangeEndDate"
                                    className="mb-2"
                                  >
                                    End Date
                                  </label>
                                  <DatePicker
                                    value={rangeEndDate}
                                    onChange={(date) =>
                                      setRangeEndDate(date as moment.Moment)
                                    }
                                    timeFormat={false}
                                    isValidDate={isValidRangeEndDate}
                                    inputProps={{
                                      id: "rangeEndDate",
                                      className: "form-control",
                                    }}
                                  />
                                </div>
                                <div className="col-md-4 d-flex align-items-end">
                                  <small className="text-muted mb-2">
                                    {rangeWeekdayCount} weekday
                                    {rangeWeekdayCount === 1 ? "" : "s"} in
                                    range — supports Send Date days 1–
                                    {Math.min(rangeWeekdayCount, MAX_SEND_DAY)}
                                  </small>
                                </div>
                              </div>
                              <small className="form-text text-muted mt-2">
                                Day N in the CSV Send Date column maps to the
                                Nth weekday in this range (weekends are
                                skipped). DMs go out at the send time below in
                                each user's timezone.
                              </small>
                              {csvSchedule ? (
                                <div className="alert alert-success py-2 px-3 mt-3 mb-0">
                                  <small>
                                    <i className="fa fa-check-circle mr-1"></i>
                                    Send Date column loaded from{" "}
                                    <strong>
                                      {csvSchedule.fileName}
                                    </strong> —{" "}
                                    {
                                      Object.keys(csvSchedule.dayByUserId)
                                        .length
                                    }{" "}
                                    users with day values
                                    {csvSchedule.invalidDayUserIds.length > 0
                                      ? `, ${csvSchedule.invalidDayUserIds.length} missing/invalid (default to Day 1)`
                                      : ""}
                                    .
                                  </small>
                                </div>
                              ) : (
                                <div className="alert alert-warning py-2 px-3 mt-3 mb-0">
                                  <small>
                                    <i className="fa fa-exclamation-circle mr-1"></i>
                                    Upload a CSV with a Send Date column (values
                                    1–10) to use this mode.
                                  </small>
                                </div>
                              )}
                            </>
                          )}
                          <div className="border-top pt-3 mt-3">
                            <SendTimeInput
                              id="sendTime"
                              value={sendSchedule}
                              onChange={setSendSchedule}
                            />
                            <small className="form-text text-muted">
                              In each user&apos;s own timezone, within 30
                              minutes. A window splits the list evenly into
                              30-minute slots, spreading each org&apos;s users
                              across all of them.
                            </small>
                          </div>
                          {campaignType.startsWith("sequence") && (
                            <div className="border-top pt-3 mt-3">
                              <div className="custom-control custom-checkbox">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="bypassPaidPlanCheck"
                                  checked={bypassPaidPlanCheck}
                                  onChange={(e) =>
                                    setBypassPaidPlanCheck(e.target.checked)
                                  }
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor="bypassPaidPlanCheck"
                                >
                                  Allow sending to paid plan users
                                </label>
                              </div>
                              <small className="form-text text-muted">
                                By default, sequence DMs are blocked for paid
                                plan users. Check this when scheduling
                                individually for paid customers.
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <hr className="my-4" />

                    <div className="d-flex justify-content-end">
                      {selectedAction === "add" && (
                        <button
                          className="btn btn-primary"
                          onClick={handleConfirmationRequest}
                          disabled={!!loadingAction}
                        >
                          {loadingAction === "add" && <Spinner />}
                          Add to Campaign
                        </button>
                      )}
                      {selectedAction === "remove" && (
                        <button
                          className="btn btn-warning text-white"
                          onClick={handleConfirmationRequest}
                          disabled={!!loadingAction}
                        >
                          {loadingAction === "remove" && <Spinner />}
                          Remove from Campaign
                        </button>
                      )}
                      {selectedAction === "optout" && (
                        <button
                          className="btn btn-danger"
                          onClick={handleConfirmationRequest}
                          disabled={!!loadingAction}
                        >
                          {loadingAction === "optout" && <Spinner />}
                          Opt Out
                        </button>
                      )}
                      {selectedAction === "delete-messages" && (
                        <button
                          className="btn btn-info text-white"
                          onClick={handleConfirmationRequest}
                          disabled={!!loadingAction}
                        >
                          {loadingAction === "delete-messages" && <Spinner />}
                          Delete Messages
                        </button>
                      )}
                    </div>

                    {successMessage && (
                      <div
                        className="alert alert-success alert-dismissible fade show mt-4"
                        role="alert"
                      >
                        {successMessage}
                        <button
                          type="button"
                          className="close"
                          onClick={() => setSuccessMessage(null)}
                          aria-label="Close"
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {confirmationDetails && (
          <div
            className="modal"
            tabIndex={-1}
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Action</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setConfirmationDetails(null)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p>Please confirm the details of your request:</p>
                  <ul className="list-group">
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      Action
                      <span className="badge badge-primary badge-pill text-capitalize">
                        {selectedAction}
                      </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      Number of Users
                      <span className="badge badge-primary badge-pill">
                        {confirmationDetails.userCount}
                      </span>
                    </li>
                    {confirmationDetails.campaignType && (
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        Campaign
                        <span>{confirmationDetails.campaignType}</span>
                      </li>
                    )}
                    {confirmationDetails.sendDate && (
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        Send Date
                        <span>{confirmationDetails.sendDate}</span>
                      </li>
                    )}
                    {confirmationDetails.sendSchedule && (
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        Send Time
                        <span>
                          {describeSendSchedule(
                            confirmationDetails.sendSchedule,
                          )}{" "}
                          in each user&apos;s timezone
                        </span>
                      </li>
                    )}
                    {confirmationDetails.bypassPaidPlanCheck && (
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        Paid Plan Override
                        <span className="badge badge-warning text-dark">
                          Enabled — will send to paid plan users
                        </span>
                      </li>
                    )}
                  </ul>
                  {scheduleRows && (
                    <div className="mt-3">
                      {(confirmationDetails.scheduleDefaultedCount || 0) >
                        0 && (
                        <div className="alert alert-warning py-2">
                          {confirmationDetails.scheduleDefaultedCount} user(s)
                          have a missing or invalid Send Date value and will
                          default to Day 1.
                        </div>
                      )}
                      <label className="font-weight-bold mb-2">
                        Schedule (dates are editable)
                      </label>
                      <table className="table table-sm table-bordered mb-2">
                        <thead>
                          <tr>
                            <th>Day</th>
                            <th style={{ width: "45%" }}>Send Date</th>
                            <th>Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scheduleRows.map((row, index) => (
                            <tr key={row.day}>
                              <td className="align-middle">Day {row.day}</td>
                              <td>
                                <DatePicker
                                  value={row.date}
                                  onChange={(date) =>
                                    updateScheduleRowDate(index, date)
                                  }
                                  timeFormat={false}
                                  isValidDate={isValidSendDay}
                                  inputProps={{
                                    id: `scheduleRowDate-${row.day}`,
                                    className: "form-control form-control-sm",
                                  }}
                                />
                              </td>
                              <td className="align-middle">
                                <span className="badge badge-primary badge-pill">
                                  {row.userCount}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <small className="form-text text-muted">
                        Adjust any date before confirming — e.g. move a day that
                        falls on a holiday.
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setConfirmationDetails(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={executeAction}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {csvConfirmation && (
          <div
            className="modal"
            tabIndex={-1}
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm CSV Upload</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setCsvConfirmation(null)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p>Please confirm the details of your CSV upload:</p>
                  <ul className="list-group">
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      File Name
                      <span>{csvConfirmation.fileName}</span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      User Count
                      <span className="badge badge-primary badge-pill">
                        {csvConfirmation.userCount}
                      </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      Send Date Column
                      {csvConfirmation.hasSendDateColumn ? (
                        <span className="badge badge-success">Detected</span>
                      ) : (
                        <span className="badge badge-secondary">
                          Not present
                        </span>
                      )}
                    </li>
                    {csvConfirmation.hasSendDateColumn &&
                      csvConfirmation.invalidDayUserIds.length > 0 && (
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          Missing/Invalid Send Date Values
                          <span className="badge badge-warning text-dark">
                            {csvConfirmation.invalidDayUserIds.length}
                          </span>
                        </li>
                      )}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setCsvConfirmation(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmCSV}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
